import { useEffect, useRef, useState, useCallback } from 'react';

const BFF_URL = import.meta.env.VITE_BFF_API_URL || 'http://localhost:8086';
const WS_URL = BFF_URL.replace(/^http/, 'ws') + '/ws';

export interface OrderEvent {
  type?: string;
  order_id: string;
  order_number: string;
  items?: Array<{
    id: string;
    name: string;
    quantity: number;
    department: string;
    price: number;
    notes?: string;
    status: string;
  }>;
  // item added
  item?: {
    id: string;
    name: string;
    quantity: number;
    department: string;
    price: number;
    notes?: string;
    status: string;
  };
  // item status change
  item_id?: string;
  item_name?: string;
  department?: string;
  old_status?: string;
  new_status?: string;
  // common
  timestamp: string;
}

type EventHandler = (event: OrderEvent) => void;

export function useOrderWebSocket(onEvent: EventHandler) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>();
  const reconnectDelay = useRef(1000);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(WS_URL);

    ws.onopen = () => {
      setConnected(true);
      reconnectDelay.current = 1000;
    };

    ws.onmessage = (e) => {
      try {
        const event = JSON.parse(e.data) as OrderEvent;
        // detect event type from payload shape
        if (event.items?.length) event.type = 'food.order.created';
        else if (event.item) event.type = 'food.order.item.added';
        else if (event.new_status) event.type = 'food.order.item.status';
        onEventRef.current(event);
      } catch {
        /* ignore malformed */
      }
    };

    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      const delay = reconnectDelay.current;
      reconnectDelay.current = Math.min(delay * 2, 30000);
      reconnectTimer.current = setTimeout(connect, delay);
    };

    ws.onerror = () => {
      ws.close();
    };

    wsRef.current = ws;
  }, []);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  return { connected };
}
